import type { AgentAnalyticsContinuation } from "./AgentAnalyticsLaunchOverview";

import React from "react";

import { ResponsiveDialog } from "@replohq/lattice/blocks/modal/ResponsiveDialog";
import { Button } from "@replohq/lattice/components/button/Button";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { motion } from "motion/react";

import { AgentAnalyticsLaunchOverview } from "./AgentAnalyticsLaunchOverview";
import { AgentLaunchOverview } from "./AgentLaunchOverview";

export function AgentLaunchModal({
  open,
  source,
  variant = "default",
  analyticsContinuation = "insights",
  onEnterBeta,
  onDecline,
}: {
  open: boolean;
  source: string;
  variant?: "analytics" | "default";
  analyticsContinuation?: AgentAnalyticsContinuation;
  onEnterBeta: () => void;
  onDecline: (method: "not_now" | "close") => void;
}) {
  const logEvent = useLogAnalytics();

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          logEvent("agent.beta.launchModal.dismissed", {
            source,
            method: "close",
          });
          onDecline("close");
        }
      }}
      accessibleTitle={
        variant === "analytics"
          ? "Analytics with Replo Agent"
          : "Introducing the new version of Replo"
      }
      size="base"
      UNSAFE_className="sm:max-w-lg"
      footer={
        <div className="flex w-full flex-col gap-2.5">
          <div className="grid w-full grid-cols-2 gap-2">
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => {
                logEvent("agent.beta.launchModal.dismissed", {
                  source,
                  method: "not_now",
                });
                onDecline("not_now");
              }}
            >
              {variant === "analytics" ? "Not now" : "Keep building here"}
            </Button>
            <Button
              className="w-full"
              onClick={() => {
                logEvent("agent.beta.launchModal.entered", { source });
                onEnterBeta();
              }}
            >
              Enable Replo Agent
            </Button>
          </div>
          <p className="text-left text-[11px] leading-tight text-placeholder">
            Creates a new project. None of your existing projects are impacted.
          </p>
        </div>
      }
    >
      <motion.div
        onViewportEnter={() => {
          logEvent("agent.beta.launchModal.viewed", { source });
        }}
        viewport={{ once: true }}
        className="max-h-[70vh] overflow-y-auto pt-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {variant === "analytics" ? (
          <AgentAnalyticsLaunchOverview continuation={analyticsContinuation} />
        ) : (
          <AgentLaunchOverview />
        )}
      </motion.div>
    </ResponsiveDialog>
  );
}
